<template>
  <div class="w-full max-h-screen bg-background flex flex-col relative">
    <page-header title="Schedules" backTo="service">
      <router-link
          :to="{ name: 'schedule-create' }"
      >

      </router-link>
    </page-header>

    <div class="p-6 bg-background flex-1 overflow-y-scroll">
      <div class="w-full bg-white p-5 rounded shadow-md relative">
        <div class="w-full flex items-center justify-between">
          <div class="flex -mx-0 mr-2">
            <TabHeaders :tabs="tabs" :selectedTab="currentTab" @tab-selected="onTabSelected"/>
          </div>
          <div class="flex flex-col flex-1">
            <div class="flex justify-end">
              <div class="flex items-center mr-4">
                <label class="mr-2">From Date:</label>
                <Calendar
                    class="w-full lg:w-auto"
                    v-model="fromDateInput"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                />
              </div>
              <div class="flex items-center ">
                <label class="mr-2">To Date:</label>
                <Calendar
                    class="w-full lg:w-auto"
                    v-model="toDateInput"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                />
              </div>

              <StaffAutocomplete class="ml-6 w-full lg:w-1/5 xl:w-1/5 2xl:w-1/5" v-model="selectedEngineerInput"
                                 :forceSelection="true"/>

              <input
                  type="text"
                  class="ml-6 w-full lg:w-auto rounded-l bge-input bge-input-spacing"
                  placeholder="Search "
                  v-model="searchTextInput"
                  @keydown.enter="triggerSearch"
              />
              <button
                  @click="triggerSearch"
                  class="rounded-r bg-blue-200 hover:bg-blue-300 border border-blue-400 hover:border-blue-500 p-2 hover:shadow active:shadow-inner transition-colors duration-300 ease-in-out"
              >
                <Icon iconType="search"/>
              </button>
            </div>
          </div>
        </div>

        <TabContent :currentTab="currentTab">
          <template v-if="currentTab === 'my_schedule'" v-slot:my_schedule>

            <DataTableComponent
                :columns="my_schedule_columns"
                @row-click="onRowClick"
                key="my_schedule"
                :enableRowClick="false"
                :enableRowExpansion="true"
                :enableHeaderExpandControls="false"
                dataKey="_row_id"
            >
              <template v-slot:schedule-date-slot="{ rowData }">
                {{ localScheduleDate(rowData.scheduled_date) }}
              </template>

              <template v-slot:schedule-day-slot="{ rowData }">
                {{ localScheduleDay(rowData.scheduled_date) }}
              </template>

              <template v-slot:owner-postcode-slot="{ rowData }">
                <div v-if="rowData.customer && rowData.customer.default_address">
                  {{ getDefaultAddressPostcode(rowData.customer.default_address) }}
                </div>
              </template>

              <template v-slot:owner-city-slot="{ rowData }">
                <div v-if="rowData.customer && rowData.customer.default_address">
                  {{ getDefaultAddressCity(rowData.customer.default_address) }}
                </div>
              </template>

              <template v-slot:equipment-count-slot="{ rowData }">
                {{ rowData.equipment_count || (rowData.equipments ? rowData.equipments.length : 0) }}
              </template>


              <template v-slot:engineer-count-slot="{ rowData }">
                <span
                    class="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    @click.stop="openEngineerPanel(rowData)"
                >
                  {{ rowData.engineer_count || 0 }}
                </span>
              </template>

              <template v-slot:job-id-slot="{ rowData }">
                <router-link
                    class="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    :to="{ name: 'my-schedule-job-details', params: { job_id: rowData.job_id } }"
                >
                  {{ rowData.job_id }}
                </router-link>
              </template>

              <template v-slot:job-type-slot="{ rowData }">
                <span class="inline-flex items-center gap-2">
                  <span>{{ formatJobType(rowData) }}</span>
                  <span
                      v-if="rowData.requires_parts_collection"
                      class="inline-flex items-center text-orange-600"
                      title="Collect parts before site visit"
                  >
                    <Icon iconType="box" class="h-4 w-4" />
                  </span>
                </span>
              </template>

            </DataTableComponent>

          </template>
        </TabContent>
      </div>
    </div>

    <portal to="overlay-outlet">
      <Panel :showing="engineerPanelOpen" :title="'Engineers — ' + engineerPanelTitle" @close="closeEngineerPanel">
        <div v-if="engineerPanelLoading" class="flex items-center justify-center py-10">
          <Spinner :color="'text-black'" :size="8" />
        </div>
        <div v-else>
          <div v-if="!engineerPanelData.length" class="text-gray-400 text-center py-8">No engineers assigned.</div>
          <div v-for="a in engineerPanelData" :key="a.assignment_id" class="mb-3 p-3 border rounded-lg">
            <div class="font-semibold">{{ a.engineer ? `${a.engineer.first_name || ''} ${a.engineer.last_name || ''}`.trim() : a.engineer_id }}</div>
            <div class="text-sm text-gray-500 mt-1">
              <span v-if="a.start_time">{{ a.start_time }}</span>
              <span v-if="a.start_time && a.end_time"> - {{ a.end_time }}</span>
              <span class="ml-3 px-2 py-0.5 rounded text-xs" :class="{
                'bg-blue-100 text-blue-700': a.status === 'Assigned',
                'bg-yellow-100 text-yellow-700': a.status === 'InProgress',
                'bg-green-100 text-green-700': a.status === 'Completed',
                'bg-gray-100 text-gray-500': a.status === 'Cancelled',
              }">{{ a.status }}</span>
            </div>
          </div>
        </div>
      </Panel>
    </portal>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import Spinner from "@/components/Spinner.vue";
import Panel from "@/components/Panel.vue";
import TabHeaders from "@/components/TabHeaders.vue";
import TabContent from "@/components/TabContent.vue";
import DataTableComponent from "@/views/HR/Engineer/MySchedule/Components/DataTableComponent.vue";
import {mapActions, mapState} from "vuex";
import Icon from "@/components/Icon.vue";
import notificationMixin from "@/mixins/notificationMixin";
import ScheduleService from "@/services/scheduleService";
import StaffAutocomplete from "@/components/AutoComplete/StaffAutoComplete.vue";
import { primaryActiveScheduleId } from "@/utils/equipmentLocks";
import { formatJobType } from "@/utils/jobTypeFormat";
import { localScheduleDate, localScheduleDay } from "@/utils/scheduleDateFormat";

const IsAuthorized = () => import("@/components/Auth/IsAuthorized.vue");

export default {
  mixins: [notificationMixin],
  components: {
    PageHeader,
    Spinner,
    Panel,
    IsAuthorized,
    TabHeaders,
    TabContent,
    DataTableComponent,
    Icon,
    StaffAutocomplete
  },
  data() {
    return {
      engineerPanelOpen: false,
      engineerPanelLoading: false,
      engineerPanelData: [],
      engineerPanelTitle: '',
      tabs: [
        {id: "my_schedule", label: "Schedules",},
      ],
      my_schedule_columns: [
        {
          field: "schedule_date",
          header: "Day",
          sortable: false,
          custom: true,
          slotName: "schedule-day-slot",
          width: '10%',
        },
        {
          field: "",
          header: "Date",
          custom: true,
          slotName: "schedule-date-slot",
          width: '10%',
        },
        {
          field: "schedule_time",
          header: "Start Time",
          sortable: false,
          custom: false,
          width: '5%',
        },
        {
          field: "schedule_end_time",
          header: "End Time",
          sortable: false,
          custom: false,
          width: '5%',
        },
        {
          field: "customer.name",
          header: "Customer",
          sortable: false,
          custom: false,
          width: '20%',
        },
        {
          field: "customer.default_address.city",
          header: "Town/City",
          sortable: false,
          custom: true,
          slotName: "owner-city-slot",
          width: '10%',
        },
        {
          field: "customer.default_address.postcode",
          header: "Postcode",
          sortable: false,
          custom: true,
          slotName: "owner-postcode-slot",
          width: '10%',
        },
        {
          field: "equipment_count",
          header: "Equipment",
          sortable: false,
          custom: true,
          slotName: "equipment-count-slot",
          width: "6%",
        },

        {
          field: "engineer_count",
          header: "Engineers",
          sortable: false,
          custom: true,
          slotName: "engineer-count-slot",
          width: '10%',
        },
        {
          field: "equipment.job_types",
          header: "Job Type",
          sortable: false,
          custom: true,
          slotName: "job-type-slot",
          width: '15%',
        },
        {
          field: "job_id",
          header: "Job ID",
          sortable: false,
          custom: true,
          slotName: "job-id-slot",
          width: '5%',
        },

      ],
    };
  },
  computed: {
    ...mapState("mySchedule", ["currentTab", "searchText", "selectedEngineer", "fromDate", "toDate"]),

    searchTextInput: {
      get() {
        return this.searchText;
      },
      set(value) {
        this.$store.commit("mySchedule/UPDATE_SEARCH_TEXT", value);
      },
    },

    selectedEngineerInput: {
      get() {
        return this.selectedEngineer;
      },
      set(value) {
        this.$store.commit("mySchedule/UPDATE_SELECTED_ENGINEER", value);
      },
    },

    fromDateInput: {
      get() {

        const fromDate = this.fromDate;
        return fromDate ? new Date(fromDate) : null;
      },
      set(value) {

        this.$store.commit("mySchedule/UPDATE_FROM_DATE", value);
      }
    },

    toDateInput: {
      get() {
        const toDate = this.toDate;
        return toDate ? new Date(toDate) : null;
      },
      set(value) {
        this.$store.commit("mySchedule/UPDATE_TO_DATE", value);
      }
    },

  },
  mounted() {
    this.fetchTableData();
  },
  methods: {
    ...mapActions("mySchedule", [
      "fetchTableData",
      "updateSearchCriteria",
      "updateTab",
    ]),

    validateDates() {
      const hasFromDate = !!this.fromDateInput;
      const hasToDate = !!this.toDateInput;

      if (hasFromDate && hasToDate) {
        if (this.toDateInput < this.fromDateInput) {
          this.notifyWarning("To Date should not be earlier than From Date.");
          return false;
        }
        return true;
      }

      if (hasFromDate !== hasToDate) {
        this.notifyWarning("Please select both the From Date and To Date.");
        return false;
      }

      return true;
    },

    getDefaultAddressPostcode(defaultAddress) {
      return defaultAddress ? defaultAddress.postcode : 'N/A';
    },
    getDefaultAddressCity(defaultAddress) {
      return defaultAddress ? defaultAddress.city : 'N/A';
    },
    getDefaultPeoplePhone(defaultPerson) {
      return defaultPerson ? defaultPerson.mobile || defaultPerson.telephone : 'N/A';
    },
    onRowClick(event) {

      this.Edit(event.data);
    },
    formatTimestamp(unixTimestamp) {
      return this.$moment.unix(unixTimestamp).format('DD-MM-YYYY');
    },
    formatJobType,
    localScheduleDate,
    localScheduleDay,
    onTabSelected(newTab) {
      this.setTab(newTab);
    },
    setTab(newTab) {
      this.$store.dispatch("mySchedule/updateTab", newTab);
    },
    triggerSearch() {
      if (this.validateDates()) {
        this.setTab("my_schedule");
      }
    },

    async openEngineerPanel(rowData) {
      this.engineerPanelOpen = true;
      this.engineerPanelLoading = true;
      this.engineerPanelData = [];
      this.engineerPanelTitle = `${rowData.job_id || ''} — ${localScheduleDate(rowData.scheduled_date)}`;
      try {
        const dateFilter = rowData.scheduled_date ? `&scheduled_date=${rowData.scheduled_date}` : '';
        const data = await ScheduleService.listAssignments(rowData.schedule_id, `?limit=100${dateFilter}`);
        this.engineerPanelData = (data.data || []).filter(a => a.status !== 'Cancelled');
      } catch (err) {
        console.error('Error loading engineers:', err);
        this.engineerPanelData = [];
      } finally {
        this.engineerPanelLoading = false;
      }
    },
    closeEngineerPanel() {
      this.engineerPanelOpen = false;
      this.engineerPanelData = [];
    },

    Edit(rowObj) {

      let routeName;
      let params = {};

      const primaryId = primaryActiveScheduleId(rowObj);
      if (primaryId) {
        routeName = "schedule-edit";
        params.schedule_id = primaryId;
      } else {
        routeName = "schedule-create";
        if (rowObj.owner_info && rowObj.owner_info.contact_id) {

          this.$router.push({
            name: routeName,
            query: {customer_id: rowObj.owner_info.contact_id}
          });
          return;
        }
      }

      this.$router.push({
        name: routeName,
        params: params,
      });
    },
  },
};
</script>

